import { useAuth } from '@redwoodjs/auth'
import {
  FetchConfigProvider,
  GraphQLHooksProvider,
  useFetchConfig,
} from '@redwoodjs/web'
import { DocumentNode, getOperationAST } from 'graphql'
import { GraphQLClient } from 'graphql-request'
import { Variables } from 'graphql-request/dist/types'
import * as React from 'react'
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query'
import { createWebStoragePersistor } from 'react-query/createWebStoragePersistor-experimental'
import { persistQueryClient } from 'react-query/persistQueryClient-experimental'

const DISABLE_RETRY_ERRORS = new Set([
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'BAD_USER_INPUT',
])

// Export these in case user wants to statically type e.g. a `beforeQuery`
export type UseCustomQueryOptions = GraphQLQueryHookOptions &
  UseQueryOptions<unknown, unknown, Variables>
export type UseCustomMutationOptions = GraphQLMutationHookOptions &
  UseMutationOptions<unknown, unknown, Variables>

const useGraphqlClient = () => {
  const { uri } = useFetchConfig()
  const graphqlClient = new GraphQLClient(uri, {
    credentials: 'include',
    mode: 'cors',
  })
  return graphqlClient
}

/**
 * Builds a query key for the given GraphQL query & variables.
 * Use when you want to access a query made by a cell.
 *
 * **Example**
 * ```ts
 * const QUERY = gql`...`
 * ...
 * const queryClient = useQueryClient()
 * queryClient.invalidateQuery(buildQueryKey(QUERY))
 * ```
 */
export const buildQueryKey = (
  query: DocumentNode,
  variables: Record<string, unknown>
) => {
  const document = getOperationAST(query)
  const name = document?.name?.value
  return [name, variables]
}

/** Returns a function which makes the given GraphQL request when invoked. */
export const useClientRequest = (
  query: DocumentNode,
  variables: Record<string, unknown>
) => {
  const { isAuthenticated, getToken, type } = useAuth()
  const graphqlClient = useGraphqlClient()

  return async (_variables?) => {
    let requestHeaders: HeadersInit = {}

    if (isAuthenticated) {
      const token = await getToken()

      requestHeaders = {
        'auth-provider': type,
        authorization: `Bearer ${token}`,
      }
    }

    return graphqlClient.request(
      query,
      { ...variables, ..._variables },
      requestHeaders
    )
  }
}

export const useCustomQuery = (
  query: DocumentNode,
  options?: UseCustomQueryOptions
) => {
  const { variables = {}, queryKey, ...rest } = options ?? {}
  const key = queryKey ?? buildQueryKey(query, variables)
  const clientRequest = useClientRequest(query, variables)

  const result = useQuery(key, clientRequest, {
    retry: (failureCount, error) => {
      if (
        DISABLE_RETRY_ERRORS.has(error?.response?.errors?.[0]?.extensions?.code)
      ) {
        // this is likely a user error, don't retry
        return false
      }

      return failureCount < 3
    },
    ...rest,
  })

  return {
    loading: result.isLoading,
    ...result,
    variables,
  }
}

export const usePrefetchQuery = (
  query: DocumentNode,
  options?: UseCustomQueryOptions
) => {
  const { variables = {}, queryKey, ...rest } = options ?? {}
  const key = queryKey ?? buildQueryKey(query, variables)
  const clientRequest = useClientRequest(query, variables)

  const queryClient = useQueryClient()

  return (opts?: UseCustomQueryOptions) =>
    queryClient.prefetchQuery(key, clientRequest, { ...rest, ...opts })
}

export const useSetQueryData = (
  query: DocumentNode,
  variables: Record<string, unknown>
) => {
  const queryClient = useQueryClient()
  const key = buildQueryKey(query, variables)

  return {
    setQueryData: (data: unknown) => {
      queryClient.setQueryData(key, data)
    },
  }
}

export const useCustomMutation = (
  query: DocumentNode,
  options?: UseCustomMutationOptions
) => {
  const { isAuthenticated, getToken, type } = useAuth()
  const graphqlClient = useGraphqlClient()

  const result = useMutation(async (variables) => {
    let requestHeaders: HeadersInit = {}

    if (isAuthenticated) {
      const token = await getToken()

      requestHeaders = {
        'auth-provider': type,
        authorization: `Bearer ${token}`,
      }
    }

    return graphqlClient.request(query, variables, requestHeaders)
  }, options)

  return [result.mutateAsync, { loading: result.isLoading, ...result }]
}

export const RedwoodReactQueryProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { currentUser } = useAuth()

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        cacheTime: 1000 * 60 * 60 * 24, // 24 hours
      },
    },
  })

  const localStoragePersistor = createWebStoragePersistor({
    storage: window.localStorage,
  })

  persistQueryClient({
    queryClient,
    persistor: localStoragePersistor,
    buster: process.env.RELEASE_VERSION,
  })

  return (
    <QueryClientProvider client={queryClient}>
      <FetchConfigProvider>
        <GraphQLHooksProvider
          useQuery={useCustomQuery}
          useMutation={useCustomMutation}
        >
          {children}
        </GraphQLHooksProvider>
      </FetchConfigProvider>
    </QueryClientProvider>
  )
}
